/**
 * Comms API client
 * Proxy: /api/comms → comms-service:3021
 *
 * comms-service is disabled in nginx (`feature_disabled:comms`) when not
 * deployed. Callers should `catch` `FeatureDisabledError` to render an empty
 * state.
 */

import { FeatureDisabledError } from './_http';

const BASE = '/api/comms';

// ── Types ────────────────────────────────────────────────────────────────────

export interface EmailConnection {
  id: string;
  user_id: string;
  provider: string;
  email_address: string;
  is_active: boolean;
  sync_state: 'running' | 'partial' | 'stopped' | 'invalid';
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmailParticipant {
  name?: string;
  email: string;
}

export interface EmailAttachment {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  messageId?: string;
  /** Content-ID for inline attachments (matches cid:xxx references in HTML body) */
  contentId?: string;
}

export interface EmailMessage {
  id: string;
  threadId: string;
  subject: string;
  from: EmailParticipant;
  to: EmailParticipant[];
  cc?: EmailParticipant[];
  body: string;
  snippet?: string;
  date: string;
  unread?: boolean;
  attachments?: EmailAttachment[];
}

export interface EmailThread {
  id: string;
  subject: string;
  participants: EmailParticipant[];
  /** ISO date of the last message, or null when the source could not supply
   *  one. Nullable on purpose (MA-374): search results are served by
   *  context-store, whose production image does not yet populate a date for
   *  email hits (MA-373). This used to be back-filled with `new Date()`, which
   *  rendered every search result as having arrived just now. Renderers must
   *  show "date unknown" for null rather than inventing a timestamp. */
  lastMessageDate: string | null;
  snippet: string;
  messageCount: number;
  messages: EmailMessage[];
  unread?: boolean;
}

export interface SendEmailPayload {
  connection_id: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body: string;
  reply_to_message_id?: string;
  entity_type?: 'contact' | 'company' | 'deal';
  entity_id?: string;
  send_at?: number;      // Unix timestamp (seconds) — schedule for future delivery
}

export interface SendEmailResult {
  messageId: string;
  threadId: string;
  trackingId: string;
  scheduleId?: string;
  status: string;
}

export interface ScheduledEmailInfo {
  id: string;
  nylas_message_id: string;
  subject: string;
  to_addresses: string[];
  sent_at: string;
  body_preview: string | null;
  from_address: string | null;
  from_name: string | null;
  created_at: string;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  variables: Record<string, string>;
  category: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * An approval on the Google path (MA-484): the send waits behind an undo window
 * and is dispatched by orchestration's MailSendWorkflow when it ends.
 */
export interface MailSendApproval {
  operation_id: string;
  approved_at?: string;
  undo_seconds?: number;
  /** ISO time the undo window closes. */
  undo_until: string;
  approver?: { kind?: string; id?: string; email?: string };
}

/** What happened at dispatch. Absent while the undo window is running. */
export interface MailSendDispatch {
  operation_id?: string;
  /** 'uncertain' means the send may or may not have gone; nothing resends it, a person decides. */
  state: 'checking' | 'sending' | 'uncertain' | 'sent';
  reason?: string | null;
  /** Reconciliation has run out of time and handed the row to a person. */
  needs_review?: boolean;
  message_id?: string;
  reconcile?: { needs_review?: boolean; attempts?: number; draft_present?: boolean | null };
  /**
   * The message Gmail sent, read back after the send and compared with the
   * approval. Written a moment after the row turns 'sent'.
   */
  content_check?: { state: 'match' | 'mismatch' | 'unverified'; checked_at?: string; differs?: string[]; reason?: string };
  /** True when the message Gmail sent is not the approved one: a person must look. */
  content_mismatch?: boolean;
}

export interface AgentDraftContext {
  approval?: MailSendApproval;
  dispatch?: MailSendDispatch;
  /** A send with no human approval (agent policy); there is no 'auto_sent' status. */
  auto_sent?: boolean;
  /** Every approval that ended without a send, and why. */
  approval_history?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

export interface AgentDraft {
  id: string;
  connection_id: string | null;
  thread_id?: string | null;
  to_addresses: string[];
  cc_addresses: string[];
  bcc_addresses: string[];
  subject: string;
  body: string;
  context: AgentDraftContext;
  /** Exactly the agent_drafts CHECK constraint's vocabulary. */
  status: 'pending_approval' | 'approved' | 'rejected' | 'sent' | 'user_draft';
  approval_note: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  schedule_id?: string | null;
  /** The Gmail draft's id when the draft has a copy in its owner's mailbox. */
  provider_draft_id?: string | null;
  sent_at: string | null;
  entity_type?: string | null;
  created_at: string;
  updated_at: string;
}

/** The fields PATCH /agent/drafts/:id accepts (only while pending_approval). */
export type AgentDraftEdit = Partial<Pick<AgentDraft, 'to_addresses' | 'cc_addresses' | 'bcc_addresses' | 'subject' | 'body'>>;

/** Approve on the Google path: 202, scheduled behind the undo window, nothing sent yet. */
export interface ApproveScheduledResult {
  status: 'approved';
  draft: AgentDraft;
  operation_id: string;
  schedule_id: string;
  undo_until: string;
  undo_seconds: number;
  tracking_id: null;
  sent_at: null;
}

/** Approve on the Nylas path: 200, sent at once. */
export interface ApproveSentResult {
  status: 'sent';
  draft: AgentDraft;
  tracking_id: string | null;
  sent_at: string | null;
}

export type ApproveDraftResult = ApproveScheduledResult | ApproveSentResult;

export interface UndoDraftResult {
  ok: true;
  undone: true;
  status: AgentDraft['status'];
  draft: AgentDraft;
}

export type ResolveDraftAction =
  | { action: 'link'; message_id: string }
  | { action: 'return' }
  | { action: 'reject' };

export interface ResolveDraftResult {
  ok: true;
  outcome: 'linked' | 'returned' | 'rejected';
  draft: AgentDraft;
}

/**
 * A non-2xx answer from comms-service. `message` is the raw body text, as the
 * plain Error used to carry, so existing callers read it unchanged; `status`
 * and `body` (parsed JSON, when it was JSON) let a caller tell 409 too_late
 * from 409 not_undoable.
 */
export class CommsHttpError extends Error {
  readonly status: number;
  readonly body: { error?: string; phase?: string; [key: string]: unknown } | null;

  constructor(status: number, text: string, statusText: string) {
    super(text || statusText);
    this.name = 'CommsHttpError';
    this.status = status;
    let parsed: CommsHttpError['body'] = null;
    try {
      const j = JSON.parse(text) as unknown;
      if (j && typeof j === 'object' && !Array.isArray(j)) parsed = j as CommsHttpError['body'];
    } catch { /* not JSON */ }
    this.body = parsed;
  }

  /** The `error` code comms-service answered with, e.g. 'too_late'. */
  get code(): string | undefined {
    return typeof this.body?.error === 'string' ? this.body.error : undefined;
  }
}

export interface ConnectionDelegate {
  delegate_user_id: string;
  delegate_email: string | null;
  granted_at: string;
}

// ── Helper ───────────────────────────────────────────────────────────────────

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const hasBody = options.body != null;
  const response = await fetch(`${BASE}${endpoint}`, {
    ...options,
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
    credentials: 'include',
  });

  if (response.status === 503) {
    const body = (await response.clone().json().catch(() => null)) as
      | { error?: string; feature?: string }
      | null;
    if (body?.error === 'feature_disabled') {
      throw new FeatureDisabledError(body.feature ?? 'unknown');
    }
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new CommsHttpError(response.status, errorText, response.statusText);
  }

  if (response.status === 204) return null as T;
  return response.json();
}

// ── Connections ──────────────────────────────────────────────────────────────

/** What the enable endpoint's refusals mean to the person who clicked (MA-481). */
export const ENABLE_REFUSALS: Record<string, string> = {
  not_a_workspace_mailbox: 'Only heyarchie.ai mailboxes can be enabled, and you are not signed in with one.',
  mailbox_never_enabled: 'Martha never acts as this mailbox.',
  own_mailbox_only: 'You can only enable your own mailbox.',
  mailbox_enabled_for_another_user: 'This mailbox is already enabled for another Martha user. Ask them to share it with you.',
  mail_provider_not_google: 'Mail still runs through the old provider here. Mailboxes can be enabled once mail moves to Google.',
  not_a_current_workspace_user: 'Google does not accept this as a current heyarchie.ai account: it may be deleted, suspended, or an alias of another account.',
  not_a_primary_workspace_address: 'This address is an alias, or is not in the heyarchie.ai directory. Enable the account it belongs to instead.',
  directory_unavailable: 'Could not check the heyarchie.ai directory just now. Try again in a minute.',
};

export function enableRefusal(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  try {
    const code = (JSON.parse(raw) as { error?: string }).error;
    if (code && ENABLE_REFUSALS[code]) return ENABLE_REFUSALS[code];
  } catch { /* not JSON */ }
  return `Could not enable your mailbox: ${raw}`;
}

export const commsConnections = {
  list: () => request<EmailConnection[]>('/connections'),

  get: (id: string) => request<EmailConnection>(`/connections/${id}`),

  create: (payload: {
    user_id: string;
    email_address: string;
    access_token: string;
    provider?: string;
  }) =>
    request<EmailConnection>('/connections', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  delete: (id: string) =>
    request<void>(`/connections/${id}`, { method: 'DELETE' }),

  sync: (id: string) =>
    request<{ status: string }>(`/connections/${id}/sync`, { method: 'POST' }),

  syncStatus: (id: string) =>
    request<{ sync_state: string; last_synced_at: string | null }>(
      `/connections/${id}/sync/status`,
    ),

  /**
   * Enable the signed-in user's heyarchie.ai mailbox (ADR-0011, MA-481). Martha
   * acts on Google directly, so there is no sign-in round trip; the server
   * accepts only the caller's own address.
   */
  enableGoogleMailbox: (email: string) =>
    request<EmailConnection>('/connections/google', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  listDelegates: (connectionId: string) =>
    request<ConnectionDelegate[]>(`/connections/${connectionId}/delegates`),

  addDelegate: (connectionId: string, email: string) =>
    request<ConnectionDelegate>(`/connections/${connectionId}/delegates`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  removeDelegate: (connectionId: string, delegateUserId: string) =>
    request<void>(`/connections/${connectionId}/delegates/${delegateUserId}`, { method: 'DELETE' }),
};

// ── Search ──────────────────────────────────────────────────────────────────

export interface CommsSearchHit {
  document: {
    id: string;
    subject?: string;
    snippet?: string;
    body?: string;
    participants?: string[];
    from_address?: string;
    to_addresses?: string[];
    direction?: string;
    status?: string;
    state?: string;
    message_count?: number;
    /** Unix seconds, or null when search-service had no date for the hit.
     *  Never absent — search-service sends an explicit null. */
    last_message_at?: number | null;
    /** Set to true by search-service exactly when last_message_at is null. */
    date_unknown?: boolean;
    [key: string]: unknown;
  };
  highlights: Array<{ field: string; snippet: string }>;
}

export interface CommsSearchResult {
  query: string;
  results: {
    threads?: {
      entity_type: string;
      found: number;
      hits: CommsSearchHit[];
    };
  };
}

/**
 * Search email threads via search-service (body + subject + participants),
 * which is backed by context-store (MA-374).
 *
 * `doc.id` is the Nylas THREAD id — search-service drops any hit it cannot
 * resolve a thread id for rather than handing back a message id, because this
 * value is what InboxPage feeds to useEnsureThreadMetadata (a WRITE) and to
 * thread fetches.
 */
export async function searchCommsThreads(q: string): Promise<EmailThread[]> {
  const res = await fetch(`/api/search/comms?q=${encodeURIComponent(q)}&entity_types=threads&limit=50`, {
    credentials: 'include',
  });
  if (!res.ok) {
    // search-service returns 503 SEARCH_UNAVAILABLE when its Typesense backend
    // is unreachable (MA-374). Throw instead of resolving to [] so react-query
    // records an error — a dead backend must not render as "no matches"
    // (MA-375).
    const body = (await res.clone().json().catch(() => null)) as
      | { error?: string; message?: string }
      | null;
    throw new Error(body?.message || body?.error || `comms search failed: ${res.status}`);
  }
  const data: CommsSearchResult = await res.json();
  const hits = data.results?.threads?.hits ?? [];
  return hits.map((hit) => {
    const doc = hit.document;
    return {
      id: doc.id,
      subject: doc.subject || '(no subject)',
      participants: (doc.participants ?? []).map((p) => ({ email: p })),
      // No date from the store means no date. Substituting `new Date()` here
      // dated every search result "just now" — the exact defect this replaces.
      lastMessageDate: doc.last_message_at
        ? new Date(doc.last_message_at * 1000).toISOString()
        : null,
      snippet: doc.snippet || '',
      messageCount: doc.message_count ?? 1,
      messages: [],
      unread: false,
    };
  });
}

// ── Email ────────────────────────────────────────────────────────────────────

export const commsEmail = {
  send: (payload: SendEmailPayload) =>
    request<SendEmailResult>('/email/send', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  listThreads: (connectionId: string, options?: { limit?: number; offset?: number; q?: string; folder?: string }) => {
    const params = new URLSearchParams({ connection_id: connectionId });
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    if (options?.q) params.set('q', options.q);
    if (options?.folder) params.set('folder', options.folder);
    return request<EmailThread[]>(`/email/threads?${params}`);
  },

  listThreadsByLabel: (connectionId: string, label: string, limit?: number) => {
    const params = new URLSearchParams({ connection_id: connectionId, label });
    if (limit) params.set('limit', String(limit));
    return request<EmailThread[]>(`/email/threads/by-label?${params}`);
  },

  getThread: (connectionId: string, threadId: string) =>
    request<EmailThread>(`/email/thread/${threadId}?connection_id=${connectionId}`),

  getScheduledForThread: (connectionId: string, threadId: string) =>
    request<{ scheduled: ScheduledEmailInfo | null }>(`/email/thread/${threadId}/scheduled?connection_id=${connectionId}`),

  cancelScheduledEmail: (connectionId: string, trackingId: string) =>
    request<{ ok: boolean }>(`/email/scheduled/${trackingId}?connection_id=${connectionId}`, { method: 'DELETE' }),

  suggestRecipients: (connectionId: string, q: string) => {
    const params = new URLSearchParams({ connection_id: connectionId, q });
    return request<Array<{ email: string; name: string | null }>>(`/email/recipients/suggest?${params}`);
  },

  markAsRead: (connectionId: string, messageId: string) =>
    request<void>(`/email/message/${messageId}/read?connection_id=${connectionId}`, { method: 'PUT' }),

  archiveThread: (connectionId: string, threadId: string) =>
    request<void>(`/email/thread/${threadId}/archive`, {
      method: 'POST',
      body: JSON.stringify({ connection_id: connectionId }),
    }),

  trashThread: (connectionId: string, threadId: string) =>
    request<void>(`/email/thread/${threadId}/trash`, {
      method: 'POST',
      body: JSON.stringify({ connection_id: connectionId }),
    }),

  markAsSpam: (connectionId: string, threadId: string) =>
    request<void>(`/email/thread/${threadId}/spam`, {
      method: 'POST',
      body: JSON.stringify({ connection_id: connectionId }),
    }),

  untrashThread: (connectionId: string, threadId: string) =>
    request<void>(`/email/thread/${threadId}/untrash`, {
      method: 'POST',
      body: JSON.stringify({ connection_id: connectionId }),
    }),

  unspamThread: (connectionId: string, threadId: string) =>
    request<void>(`/email/thread/${threadId}/unspam`, {
      method: 'POST',
      body: JSON.stringify({ connection_id: connectionId }),
    }),

  /** Build attachment download URL (direct browser fetch — returns binary) */
  attachmentDownloadUrl: (connectionId: string, attachmentId: string, messageId: string, filename?: string) =>
    `${BASE}/email/attachment/${attachmentId}/download?connection_id=${encodeURIComponent(connectionId)}&message_id=${encodeURIComponent(messageId)}${filename ? `&filename=${encodeURIComponent(filename)}` : ''}`,
};

// ── Templates ────────────────────────────────────────────────────────────────

export const commsTemplates = {
  list: (category?: string) => {
    const params = category ? `?category=${encodeURIComponent(category)}` : '';
    return request<EmailTemplate[]>(`/templates${params}`);
  },

  get: (id: string) => request<EmailTemplate>(`/templates/${id}`),

  create: (payload: { name: string; subject: string; body: string; variables?: Record<string, string>; category?: string }) =>
    request<EmailTemplate>('/templates', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  update: (id: string, payload: Partial<EmailTemplate>) =>
    request<EmailTemplate>(`/templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  delete: (id: string) =>
    request<void>(`/templates/${id}`, { method: 'DELETE' }),

  render: (id: string, values: Record<string, string>) =>
    request<{ subject: string; body: string }>(`/templates/${id}/render`, {
      method: 'POST',
      body: JSON.stringify({ values }),
    }),
};

// ── Thread Notes ─────────────────────────────────────────────────────────────

export interface ThreadNote {
  id: string;
  thread_id: string;
  author_id: string;
  author_type: 'user' | 'agent';
  author_name: string | null;
  author_email: string | null;
  author_avatar: string | null;
  body: string;
  mentions: Array<{id: string; name: string; type: 'user' | 'agent'}>;
  parent_id: string | null;
  is_pinned: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface CreateNotePayload {
  author_id: string;
  author_name: string;
  author_email?: string;
  body: string;
  mentions?: Array<{id: string; name: string; type: 'user' | 'agent'}>;
}

// ── Agent Drafts ─────────────────────────────────────────────────────────────

export const commsDrafts = {
  list: (options?: { status?: string; connection_id?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.connection_id) params.set('connection_id', options.connection_id);
    if (options?.limit) params.set('limit', String(options.limit));
    const qs = params.toString();
    return request<AgentDraft[]>(`/agent/drafts${qs ? `?${qs}` : ''}`);
  },

  get: (id: string) => request<AgentDraft>(`/agent/drafts/${id}`),

  create: (payload: {
    to_addresses: string[];
    subject: string;
    body: string;
    connection_id?: string;
    status?: 'pending_approval' | 'user_draft';
    context?: Record<string, unknown>;
  }) =>
    request<AgentDraft>('/agent/drafts', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  update: (id: string, payload: AgentDraftEdit) =>
    request<AgentDraft>(`/agent/drafts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  /**
   * Google path: 202 { status: 'approved', operation_id, undo_until, ... }, the
   * send waits behind the undo window. Nylas path: 200 { status: 'sent', ... }.
   */
  approve: (id: string) =>
    request<ApproveDraftResult>(`/agent/drafts/${id}/approve`, { method: 'POST' }),

  /** keepMailboxDraft hands the draft to its owner: Martha never sends it and leaves the Gmail draft. */
  reject: (id: string, note?: string, opts?: { keepMailboxDraft?: boolean }) =>
    request<AgentDraft>(`/agent/drafts/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note, ...(opts?.keepMailboxDraft ? { keep_mailbox_draft: true } : {}) }),
    }),

  /**
   * Undo inside the window. 409 { error: 'too_late', phase } once dispatch has
   * started; 409 { error: 'not_undoable' } when there is nothing to undo; 403
   * when the draft is not the caller's.
   */
  undo: (id: string) =>
    request<UndoDraftResult>(`/agent/drafts/${id}/undo`, { method: 'POST' }),

  /**
   * Settle an approved send whose outcome is unknown (dispatch.state
   * 'uncertain'): link the message that went, return the draft for review, or
   * reject it. 409 { error: 'not_resolvable' } | { error: 'draft_gone_use_link_or_reject' }.
   */
  resolve: (id: string, action: ResolveDraftAction) =>
    request<ResolveDraftResult>(`/agent/drafts/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify(action),
    }),

  /** The 'Sent by Martha' feed: sends made with no human approval. */
  listSent: (limit?: number) =>
    request<AgentDraft[]>(`/agent/sent${limit ? `?limit=${limit}` : ''}`),
};

// ── Thread Metadata ──────────────────────────────────────────────────────────

export type ThreadState = 'new' | 'agent_triaging' | 'needs_review'
  | 'agent_responding' | 'pending_approval' | 'resolved';

export interface ThreadMetadata {
  id: string;
  thread_id: string;
  connection_id: string;
  state: ThreadState;
  assignee_id: string | null;
  assignee_type: 'user' | 'agent' | null;
  assigned_by: string | null;
  assigned_at: string | null;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  priority_reason: string | null;
  labels: string[];
  snoozed_until: string | null;
  first_response_at: string | null;
  resolved_at: string | null;
  last_agent_action: string | null;
  last_agent_action_at: string | null;
  agent_confidence: number | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ThreadCounts {
  inbox: number;
  assigned_to_me: number;
  shared_with_me: number;
  drafts: number;
  snoozed: number;
  archived: number;
  total: number;
}

export interface ThreadStateLogEntry {
  id: string;
  from_state: string | null;
  to_state: string;
  actor_id: string;
  actor_type: string;
  reason: string | null;
  created_at: string;
}

export const commsThreadMeta = {
  counts: (connectionId: string) =>
    request<ThreadCounts>(`/threads/counts?connection_id=${connectionId}`),

  listByState: (connectionId: string, state: string) =>
    request<string[]>(`/threads/by-state?connection_id=${connectionId}&state=${state}`),

  get: (threadId: string, connectionId: string) =>
    request<ThreadMetadata>(`/threads/metadata/${threadId}?connection_id=${connectionId}`),

  update: (threadId: string, payload: Record<string, unknown>) =>
    request<ThreadMetadata>(`/threads/metadata/${threadId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  history: (threadId: string) =>
    request<ThreadStateLogEntry[]>(`/threads/metadata/${threadId}/history`),

  ensureMetadata: (connectionId: string, threadIds: string[], defaultLabels?: string[]) =>
    request<{ ok: boolean }>(`/threads/ensure-metadata`, {
      method: 'POST',
      body: JSON.stringify({ connection_id: connectionId, thread_ids: threadIds, default_labels: defaultLabels }),
    }),

  seedStates: (connectionId: string) =>
    request<{ seeded: number }>(`/threads/seed-states`, {
      method: 'POST',
      body: JSON.stringify({ connection_id: connectionId }),
    }),

  snooze: (threadId: string, connectionId: string, snoozedUntil: string) =>
    request<ThreadMetadata>(`/threads/metadata/${threadId}/snooze`, {
      method: 'POST',
      body: JSON.stringify({ connection_id: connectionId, snoozed_until: snoozedUntil }),
    }),

  unsnooze: (threadId: string, connectionId: string) =>
    request<ThreadMetadata>(`/threads/metadata/${threadId}/unsnooze`, {
      method: 'POST',
      body: JSON.stringify({ connection_id: connectionId }),
    }),

  unarchive: (threadId: string, connectionId: string) =>
    request<ThreadMetadata>(`/threads/metadata/${threadId}/unarchive`, {
      method: 'POST',
      body: JSON.stringify({ connection_id: connectionId }),
    }),

  untrash: (threadId: string, connectionId: string) =>
    request<ThreadMetadata>(`/threads/metadata/${threadId}/untrash`, {
      method: 'POST',
      body: JSON.stringify({ connection_id: connectionId }),
    }),

  unspam: (threadId: string, connectionId: string) =>
    request<ThreadMetadata>(`/threads/metadata/${threadId}/unspam`, {
      method: 'POST',
      body: JSON.stringify({ connection_id: connectionId }),
    }),
};

// ── Notes ───────────────────────────────────────────────────────────────────

export const commsNotes = {
  list: (threadId: string) =>
    request<ThreadNote[]>(`/threads/${threadId}/notes`),

  create: (threadId: string, payload: CreateNotePayload) =>
    request<ThreadNote>(`/threads/${threadId}/notes`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  delete: (threadId: string, noteId: string, authorId: string) =>
    request<void>(`/threads/${threadId}/notes/${noteId}`, {
      method: 'DELETE',
      body: JSON.stringify({ author_id: authorId }),
    }),

  togglePin: (threadId: string, noteId: string) =>
    request<ThreadNote>(`/threads/${threadId}/notes/${noteId}/pin`, {
      method: 'PATCH',
    }),
};

// ── Email AI Compose ──────────────────────────────────────────────────────────

export interface EmailComposeRequest {
  instruction: string;
  threadMessages?: Array<{ from: string; date: string; body: string; snippet: string }>;
  contactContext?: { name: string; company: string; email: string };
  currentDraft?: string;
  /** Yjs room ID — enables Martha presence (thinking/editing/idle) during compose */
  roomId?: string;
}

export interface EmailComposeResponse {
  html: string;
  text: string;
  summary: string;
}

const ORCH_BASE = '/api/orchestration';

export const commsAiCompose = {
  /**
   * Stream Martha's compose output as it arrives. `onToken` fires for each
   * text chunk so the editor can show progressive typing. The promise resolves
   * with the final structured result once Martha finishes.
   */
  compose: async (payload: EmailComposeRequest, onToken?: (chunk: string) => void): Promise<EmailComposeResponse> => {
    const res = await fetch(ORCH_BASE + '/email/compose', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok || !res.body) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || `Compose failed: ${res.status}`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let done: EmailComposeResponse | null = null;
    let errMsg: string | null = null;
    while (true) {
      const { done: streamDone, value } = await reader.read();
      if (streamDone) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      let currentEvent = '';
      let currentData = '';
      for (const line of lines) {
        if (line.startsWith(':')) continue;
        if (line.startsWith('event: ')) currentEvent = line.slice(7).trim();
        else if (line.startsWith('data: ')) currentData = line.slice(6);
        else if (line === '' && currentEvent && currentData) {
          try {
            const parsed = JSON.parse(currentData);
            if (currentEvent === 'token' && typeof parsed.text === 'string') {
              onToken?.(parsed.text);
            } else if (currentEvent === 'done') {
              done = parsed as EmailComposeResponse;
            } else if (currentEvent === 'error') {
              errMsg = parsed.error || 'Compose failed';
            }
          } catch { /* skip malformed */ }
          currentEvent = '';
          currentData = '';
        }
      }
    }
    if (errMsg) throw new Error(errMsg);
    if (!done) throw new Error('Compose stream ended without a final result');
    return done;
  },
};
